import { Confirm } from './Confirm';
import { ErrorNote } from './ui';

/**
 * Deleting a retired record, and undoing that.
 *
 * Deleting hides a record from its organization completely and takes its
 * history with it: reports and past registers join these tables inline, so the
 * marks go too and the rates for those dates change. That is a bigger thing
 * than retiring, so it asks first, and it asks with a number rather than in the
 * abstract. "This changes attendance figures" is not something anybody can act
 * on; "this hides 240 marks" is.
 *
 * The question is the app's shared confirmation, hung off the Delete button
 * (see Confirm.tsx). A refused delete is shown inside it; `pending` and `error`
 * here belong to Restore, which is one click and reports beside itself.
 */
export function DeleteRecord({
  noun,
  markCount,
  deleted,
  canRestore,
  onDelete,
  onRestore,
  pending,
  error,
}: {
  /** How the prose names it: "period", "class", "student", "account". */
  noun: string;
  /**
   * Marks that would go with it. Omit where deleting hides none: an account's
   * past marks are attributed by a nullable reference no report joins.
   */
  markCount?: number;
  deleted: boolean;
  /** Only a platform administrator may undo a delete. */
  canRestore: boolean;
  /** Resolves once deleted; a rejection is shown in the confirmation. */
  onDelete: () => Promise<unknown>;
  onRestore: () => void;
  /** Restore in flight. */
  pending: boolean;
  /** A refused restore. */
  error: string | null;
}) {
  if (deleted) {
    return (
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="eyebrow text-absent">deleted</span>
          {canRestore ? (
            <button type="button" className="btn" disabled={pending} onClick={onRestore}>
              {pending ? 'Restoring…' : 'Restore'}
            </button>
          ) : (
            <span className="text-sm text-ink-soft">
              Only a platform administrator can restore this.
            </span>
          )}
        </div>
        {error ? <ErrorNote message={error} /> : null}
      </div>
    );
  }

  return (
    <Confirm
      trigger="Delete"
      title={`Delete this ${noun}?`}
      description={
        markCount === undefined ? (
          <>
            Deleting this {noun} hides it from this organization. It stays in the database, and only
            a platform administrator can bring it back.
          </>
        ) : markCount === 0 ? (
          <>
            Nothing has been marked against this {noun}, so deleting it changes no attendance
            figures. It stays in the database, and only a platform administrator can bring it back.
          </>
        ) : (
          <>
            Deleting this {noun} hides it and its{' '}
            <strong className="font-mono text-ink tabular">{markCount}</strong>{' '}
            {markCount === 1 ? 'mark' : 'marks'} from this organization. Attendance rates for those
            dates will change. Only a platform administrator can undo this.
          </>
        )
      }
      confirmLabel={`Delete this ${noun}`}
      pendingLabel="Deleting…"
      onConfirm={onDelete}
      errorFallback={`Could not delete this ${noun}.`}
    />
  );
}
